import { useTranslation } from 'react-i18next';
import type { DeviceChannel, DeviceSummary } from '../deviceModel';
import { accessibleChannels, canControlChannel } from '../deviceModel';
import { CHANNEL_SIGNAL_FORMAT, CHANNEL_VIDEO_LOSS_ENABLED, CHANNEL_VIDEO_SIGNAL_PRESENT } from './ids';
import { useAttr } from './ops';
import { ReadonlyRow, SectionCard, ToggleRow } from './widgets';

function ChannelRow({ device, channel, wsOpen }: { device: DeviceSummary; channel: DeviceChannel; wsOpen: boolean }) {
  const { t } = useTranslation();
  const enabled = useAttr(device.device_id, CHANNEL_VIDEO_LOSS_ENABLED, wsOpen, channel.ch);
  const signal = useAttr(device.device_id, CHANNEL_VIDEO_SIGNAL_PRESENT, wsOpen, channel.ch);
  const format = useAttr(device.device_id, CHANNEL_SIGNAL_FORMAT, wsOpen, channel.ch);
  const canControl = canControlChannel(device, channel.ch);
  const label = channel.name?.trim() || t('deviceSettings.channelNumber', { channel: channel.ch });

  return (
    <div data-testid={`device-channel-${channel.ch}`} style={{ borderTop: '1px solid var(--color-surface-line)', paddingTop: 'var(--spacing-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--spacing-4)', alignItems: 'center' }}>
        <strong>{label}</strong>
        <span className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
          {channel.enabled ? t('deviceSettings.channelEnabled') : t('deviceSettings.channelDisabled')} · {channel.source}
        </span>
      </div>
      <ReadonlyRow
        label={t('deviceSettings.videoSignal')}
        attr={signal}
        render={(v) => (v === true || v === 1 ? t('deviceSettings.signalPresent') : t('deviceSettings.videoLoss'))}
      />
      <ReadonlyRow label={t('deviceSettings.signalFormat')} attr={format} />
      <ToggleRow
        label={t('deviceSettings.videoLossDetection')}
        hint={!canControl ? t('deviceSettings.channelReadOnly') : undefined}
        attr={enabled}
        disabled={!canControl || !channel.enabled}
      />
    </div>
  );
}

export function ChannelSection({ device, wsOpen }: { device: DeviceSummary; wsOpen: boolean }) {
  const { t } = useTranslation();
  const channels = accessibleChannels(device);
  return (
    <SectionCard title={t('deviceSettings.channels')}>
      {channels.length === 0 ? (
        <p className="text-secondary" role="status" style={{ margin: 0 }}>
          {t('deviceSettings.noAuthorizedChannels')}
        </p>
      ) : (
        channels.map((channel) => <ChannelRow key={channel.ch} device={device} channel={channel} wsOpen={wsOpen} />)
      )}
    </SectionCard>
  );
}
